import { Trans, useLingui } from "@lingui/react/macro";
import { SearchIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";

import { useCountries } from "../../catalogs/countries";
import { PatientResults } from "../../components/patient-search-palette";
import { resolveEnumLabel } from "../../components/ui-kit/fields/utils";
import {
  MAX_SEARCH_RESULTS,
  SearchPalette,
} from "../../components/ui-kit/search-palette";
import { ShortcutHint } from "../../components/ui-kit/shortcut-hint";
import { monitoringTypes, patientStatuses } from "../../constants/enum-to-text";
import { cn } from "../../utils/shadcn-ui";
import { patientPath } from "./patient-path";
import { PatientSearchModel } from "./patient-search-model";

export const PatientSearch = observer(function PatientSearch({
  model,
}: {
  model: PatientSearchModel;
}) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const countries = useCountries();

  function handleSelect(patientId: number) {
    model.close();
    const patient = model.patients.find((p) => p.patient_id === patientId);
    void navigate(patient ? patientPath(patient) : `/patient/${patientId}`);
  }

  // The whole patient list is held in memory, so an empty query would render
  // every patient there is. Capped like every other palette.
  const matches = model.filteredPatients;

  // The palette's own trigger is sized for a page header; this one sits in the
  // top bar, so it brings its own. SearchPalette still owns the dialog, the ⌘K
  // binding and the result cap.
  const trigger = (
    <>
      {/* A field, not a well: it takes the kit's own input surface and radius,
          so a control that opens a search box looks like one, and the shortcut
          is held back until you reach for it.

          `rounded-md` is the radius every field in the kit carries (base-input,
          base-combobox, tags-input) and the one the bar's own controls use.

          The surface is `--sidebar-accent`, the chrome's own interactive
          surface — what every nav row takes on hover. One token covers both
          themes because this is the only near-white in the chrome that steps
          the right way in each: 1.137:1 off the bar in light at 0.013 chroma,
          and its dark value lifts to oklch(0.32) against the bar's 0.27.
          `--muted` and `--accent` both collapse into the bar in dark, which is
          what the `bg-black/[0.045]` alpha this replaces was working around.

          Three others were tried here. `bg-white dark:bg-input/30`, the kit's
          own field surface, read as flat as the bar (1.055:1). That alpha read
          as a recess rather than as a control. `--nav-active` had the most
          presence of any of them (1.202:1) but it is the colour that means
          "current page" in the nav, so the field read as selected — and the
          brand ink over it fell to 4.41:1, under the AA floor for text, where
          over this it clears at 4.66:1 in light and 4.84:1 in dark.

          Widths and heights are split one utility per breakpoint (`h-8 nav:h-9`,
          not `size-8` plus an override): two conflicting `width` utilities at
          the same breakpoint get resolved by Tailwind's own utility order rather
          than the order they are written in. The width is a clamp because the
          bar centres this on the header's true middle, which puts it out of
          reach of flexbox shrinking — it has to give way on its own. */}
      <button
        type="button"
        onClick={() => void model.open()}
        aria-keyshortcuts="Meta+K Control+K"
        className={cn(
          // Named group, like the nav's rows: the sidebar root is itself a
          // `group`, and an unnamed one here would be a trap waiting for the
          // next person to nest something.
          "group/search relative flex h-8 w-8 min-w-0 cursor-pointer items-center justify-center rounded-md outline-none",
          "bg-sidebar-accent",
          // Transparent at rest rather than absent: a border that only exists
          // on hover would add 1px to the box and nudge the icon and the label
          // sideways as the pointer arrives. Focus takes the same edge as
          // hover — the ring alone left a keyboard user without the cue the
          // pointer gets.
          "border border-transparent hover:border-brand-primary focus-visible:border-brand-primary",
          // 140ms plain `ease`: the chrome's hover timing, deliberately not the
          // 280ms curve it uses for motion (see rail-menu.tsx). The icon and
          // label below carry the same transition — a parent's does not cascade.
          "transition-colors duration-140 ease-[ease]",
          "focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          "nav:h-9 nav:w-[clamp(10rem,24vw,20rem)] nav:gap-2.5 nav:px-4",
        )}
      >
        <SearchIcon className="text-nav-icon group-hover/search:text-brand-primary group-focus-visible/search:text-brand-primary size-4 shrink-0 transition-colors duration-140 ease-[ease]" />
        <span className="text-sidebar-foreground/70 group-hover/search:text-brand-primary group-focus-visible/search:text-brand-primary truncate text-[13px] transition-colors duration-140 ease-[ease] max-nav:sr-only">
          <Trans>Search patients</Trans>
        </span>
        {/* Out of the flow, pinned to the right edge: in the flow its width
            would count against the row and push the centred icon and label off
            the well's middle. Its opacity, not its presence, is what changes,
            so nothing shifts when it arrives either way. On focus as well as
            hover: a keyboard user is exactly who the shortcut is for, and would
            otherwise never be shown it. */}
        <ShortcutHint className="hidden opacity-0 transition-opacity duration-140 ease-[ease] group-hover/search:opacity-100 group-focus-visible/search:opacity-100 nav:absolute nav:top-1/2 nav:right-4 nav:block nav:-translate-y-1/2">
          ⌘K
        </ShortcutHint>
      </button>
    </>
  );

  return (
    <SearchPalette
      open={model.isOpen}
      onOpenChange={(open) => {
        if (open) void model.open();
        else model.close();
      }}
      query={model.query}
      onQueryChange={(v) => {
        model.query = v;
      }}
      trigger={trigger}
      inputPlaceholder={t`Search by name or ID...`}
      title={t`Patient Search`}
      description={t`Search patients by name or ID`}
      loading={model.loading}
      emptyMessage={t`No patients found.`}
    >
      <PatientResults
        results={matches.slice(0, MAX_SEARCH_RESULTS).map((patient) => {
          const regionSchema =
            patient.region != null
              ? countries.getRegionSchema(patient.country, patient.region)
              : undefined;
          return {
            id: patient.patient_id,
            name: patient.full_name,
            subtitle: [
              patient.monitoring_type != null &&
                resolveEnumLabel(monitoringTypes[patient.monitoring_type]),
              patient.patient_status != null &&
                resolveEnumLabel(patientStatuses[patient.patient_status]),
              [patient.country, regionSchema?.region_name]
                .filter(Boolean)
                .join(" / "),
            ],
          };
        })}
        matchCount={matches.length}
        onSelect={handleSelect}
      />
    </SearchPalette>
  );
});
